import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Card, CardBody, Typography } from '@material-tailwind/react';
import ReactSelect from 'react-select';
import { FaArrowLeft, FaCalendarAlt, FaCheckCircle, FaChevronDown, FaFileInvoiceDollar, FaPlay, FaSave, FaSyncAlt, FaTimesCircle } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../shared/formatMoney';
import {
  buildDefaultInvoiceNotes,
  formatDate,
  formatPeriodLabel,
  getApiErrorMessage,
  getOwnerCodeFromItem,
  normalizeOwnerCode
} from './bulk-monthly-invoices/helpers';

const BulkMonthlyInvoices = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const authToken = token || localStorage.getItem('authToken');
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState({ value: currentYear, label: String(currentYear) });
  const [selectedMonth, setSelectedMonth] = useState({
    value: currentMonth,
    label: new Date(currentYear, currentMonth - 1, 1).toLocaleString('default', { month: 'long' })
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [savingSelectedReady, setSavingSelectedReady] = useState(false);
  const [previewResults, setPreviewResults] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saveSummary, setSaveSummary] = useState(null);
  const [selectedReadyOwnerCodes, setSelectedReadyOwnerCodes] = useState([]);
  const [expandedOwnerCodes, setExpandedOwnerCodes] = useState([]);
  const [notes, setNotes] = useState('');
  const [selectedDraftInvoiceRefs, setSelectedDraftInvoiceRefs] = useState([]);
  const [sendingDrafts, setSendingDrafts] = useState(false);
  const errorSectionRef = useRef(null);

  const monthOptions = useMemo(() => (
    Array.from({ length: 12 }, (_, index) => {
      const monthValue = index + 1;
      return {
        value: monthValue,
        label: new Date(2000, monthValue - 1, 1).toLocaleString('default', { month: 'long' })
      };
    })
  ), []);

  const yearOptions = useMemo(() => (
    Array.from({ length: 7 }, (_, index) => {
      const yearValue = currentYear - 3 + index;
      return { value: yearValue, label: String(yearValue) };
    })
  ), [currentYear]);

  const selectStyles = useMemo(() => ({
    control: (base) => ({
      ...base,
      minHeight: '40px',
      borderColor: '#d6d3d1',
      boxShadow: 'none'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 30
    })
  }), []);

  const primaryInvoiceButtonClass =
    'inline-flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 py-2 shadow-none transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  const rowInvoiceButtonClass =
    'inline-flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-full px-4 py-2 shadow-none transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  const secondaryInvoiceButtonClass =
    'inline-flex items-center gap-2 border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white text-sm font-semibold rounded-sm px-4 py-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  const neutralInvoiceButtonClass =
    'inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-sm px-4 py-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';

  const selectedPeriodLabel = useMemo(
    () => formatPeriodLabel(selectedYear?.value, selectedMonth?.value),
    [selectedMonth?.value, selectedYear?.value]
  );

  useEffect(() => {
    const defaultNotes = buildDefaultInvoiceNotes(selectedPeriodLabel);
    setNotes((currentNotes) => {
      const trimmedCurrentNotes = String(currentNotes || '').trim();
      if (!trimmedCurrentNotes) {
        return defaultNotes;
      }

      const previousPrefix = 'Monthly invoice for the month of';
      if (trimmedCurrentNotes.startsWith(previousPrefix)) {
        return defaultNotes;
      }

      return currentNotes;
    });
  }, [selectedPeriodLabel]);

  const loadPreview = useCallback(async ({ preserveSaveSummary = false } = {}) => {
    if (!selectedYear?.value || !selectedMonth?.value) {
      setError('Please select both a year and month.');
      return;
    }

    setLoadingPreview(true);
    setError('');
    setMessage('');
    if (!preserveSaveSummary) {
      setSaveSummary(null);
    }

    try {
      const response = await axios.post(
        `${rootUrl}/api/job-cards/monthly-preview-bulk`,
        {
          year: selectedYear.value,
          month: selectedMonth.value
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data?.success) {
        const nextPreviewResults = Array.isArray(response.data.data) ? response.data.data : [];
        setPreviewResults(nextPreviewResults);
        setSelectedReadyOwnerCodes(
          nextPreviewResults
            .filter((item) => item?.success && !item?.data?.invoice_ref && Boolean(getOwnerCodeFromItem(item)))
            .map((item) => getOwnerCodeFromItem(item))
        );
        setMessage(response.data?.message || 'Bulk preview loaded.');
      } else {
        setPreviewResults([]);
        setSelectedReadyOwnerCodes([]);
        setError(response.data?.message || 'Failed to load bulk preview.');
      }
    } catch (err) {
      setPreviewResults([]);
      setSelectedReadyOwnerCodes([]);
      setError(getApiErrorMessage(err, 'Failed to load bulk preview.'));
    } finally {
      setLoadingPreview(false);
    }
  }, [authToken, selectedMonth?.value, selectedYear?.value]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const previewRows = useMemo(() => Array.isArray(previewResults) ? previewResults : [], [previewResults]);
  const selectedReadySet = useMemo(
    () => new Set(selectedReadyOwnerCodes.map((code) => normalizeOwnerCode(code))),
    [selectedReadyOwnerCodes]
  );
  const selectedDraftSet = useMemo(
    () => new Set((selectedDraftInvoiceRefs || []).map((id) => String(id || '').trim())),
    [selectedDraftInvoiceRefs]
  );
  const expandedOwnerSet = useMemo(
    () => new Set(expandedOwnerCodes.map((code) => normalizeOwnerCode(code))),
    [expandedOwnerCodes]
  );
  const readyInvoiceRows = useMemo(
    () => previewRows.filter((item) => {
      const ownerCode = getOwnerCodeFromItem(item);
      return item?.success && !item?.data?.invoice_ref && Boolean(ownerCode);
    }),
    [previewRows]
  );
  const draftInvoiceRows = useMemo(
    () => previewRows.filter((item) => {
      const data = item?.data || {};
      const invoiceRef = data.invoice_ref || data.trans_id || null;
      const status = String(data.invoice_status || '').toUpperCase();
      return invoiceRef && status === 'DRAFT';
    }),
    [previewRows]
  );
  const selectedReadyRows = useMemo(
    () => readyInvoiceRows.filter((item) => selectedReadySet.has(getOwnerCodeFromItem(item))),
    [readyInvoiceRows, selectedReadySet]
  );
  const hasReadyInvoiceRows = readyInvoiceRows.length > 0;
  const allReadySelected = hasReadyInvoiceRows && selectedReadyRows.length === readyInvoiceRows.length;
  const selectedReadyCount = selectedReadyRows.length;

  const toggleReadySelection = useCallback((ownerCode) => {
    const normalizedOwnerCode = normalizeOwnerCode(ownerCode);
    if (!normalizedOwnerCode) return;

    setSelectedReadyOwnerCodes((previous) => (
      previous.some((code) => normalizeOwnerCode(code) === normalizedOwnerCode)
        ? previous.filter((code) => normalizeOwnerCode(code) !== normalizedOwnerCode)
        : [...previous, normalizedOwnerCode]
    ));
  }, []);

  const toggleExpandedRow = useCallback((ownerCode) => {
    const normalizedOwnerCode = normalizeOwnerCode(ownerCode);
    if (!normalizedOwnerCode) return;

    setExpandedOwnerCodes((previous) => (
      previous.some((code) => normalizeOwnerCode(code) === normalizedOwnerCode)
        ? previous.filter((code) => normalizeOwnerCode(code) !== normalizedOwnerCode)
        : [...previous, normalizedOwnerCode]
    ));
  }, []);

  const toggleSelectAllReady = useCallback(() => {
    if (!hasReadyInvoiceRows) return;

    setSelectedReadyOwnerCodes(
      allReadySelected
        ? []
        : readyInvoiceRows.map((item) => getOwnerCodeFromItem(item)).filter(Boolean)
    );
  }, [allReadySelected, hasReadyInvoiceRows, readyInvoiceRows]);

  const toggleDraftSelection = useCallback((invoiceRef) => {
    if (!invoiceRef) return;

    setSelectedDraftInvoiceRefs((previous) => (
      previous.some((r) => String(r || '') === String(invoiceRef || ''))
        ? previous.filter((r) => String(r || '') !== String(invoiceRef || ''))
        : [...previous, String(invoiceRef || '')]
    ));
  }, []);

  const toggleSelectAllDrafts = useCallback(() => {
    if (!draftInvoiceRows || draftInvoiceRows.length === 0) return;

    setSelectedDraftInvoiceRefs((previous) => (
      previous.length === draftInvoiceRows.length
        ? []
        : draftInvoiceRows.map((item) => (item?.data?.invoice_ref || item?.data?.trans_id))
    ));
  }, [draftInvoiceRows]);

  const markSelectedDraftsSent = useCallback(async () => {
    const selectedRefs = Array.isArray(selectedDraftInvoiceRefs) ? selectedDraftInvoiceRefs : [];
    if (selectedRefs.length === 0) {
      setError('Select at least one draft invoice to mark as sent.');
      return false;
    }

    if (!window.confirm(`Mark ${selectedRefs.length} selected draft invoice(s) as sent?`)) {
      return false;
    }

    setSendingDrafts(true);
    setError('');
    setMessage('');

    try {
      const items = previewRows
        .map((item) => ({ invoice_ref: item?.data?.invoice_ref || item?.data?.trans_id, owner_code: item?.owner_code || item?.data?.owner_code }))
        .filter((it) => it.invoice_ref && selectedRefs.includes(String(it.invoice_ref)));

      const response = await axios.post(
        `${rootUrl}/api/owners/transactions/monthly-invoice/status/bulk`,
        {
          items,
          status: 'SENT'
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data?.success) {
        setMessage(response.data?.message || 'Invoices updated');
        setSelectedDraftInvoiceRefs([]);
        await loadPreview({ preserveSaveSummary: true });
        return true;
      }

      setError(response.data?.message || 'Failed to update invoices');
      return false;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update invoices'));
      return false;
    } finally {
      setSendingDrafts(false);
    }
  }, [authToken, loadPreview, previewRows, selectedDraftInvoiceRefs]);

  const postMonthlyInvoices = useCallback(async ({
    ownerCodes,
    confirmText,
    successMessage,
    failureMessage,
    setLoading
  }) => {
    const normalizedOwnerCodes = ownerCodes.map((code) => normalizeOwnerCode(code)).filter(Boolean);

    if (normalizedOwnerCodes.length === 0) {
      setError('Select at least one owner.');
      return false;
    }

    if (confirmText && !window.confirm(confirmText(normalizedOwnerCodes.length))) {
      return false;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(
        `${rootUrl}/api/job-cards/monthly-generate`,
        {
          year: selectedYear.value,
          month: selectedMonth.value,
          owner_codes: normalizedOwnerCodes,
          notes
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (response.data?.success) {
        const created = Array.isArray(response.data.data)
          ? response.data.data.filter((item) => item?.success).length
          : 0;
        const summaryData = response.data?.summary || {
          total: Array.isArray(response.data.data) ? response.data.data.length : ownerCodes.length,
          created,
          skipped: Array.isArray(response.data.data) ? response.data.data.filter((item) => item && !item.success && !item.can_retry).length : 0,
          failed: Array.isArray(response.data.data) ? response.data.data.filter((item) => item && !item.success && item.can_retry).length : 0
        };

        setSaveSummary(summaryData);
        setMessage(response.data?.message || successMessage);
        await loadPreview({ preserveSaveSummary: true });
        return true;
      }

      setError(response.data?.message || failureMessage);
      return false;
    } catch (err) {
      setError(getApiErrorMessage(err, failureMessage));
      return false;
    } finally {
      setLoading(false);
    }
  }, [authToken, loadPreview, notes, selectedMonth?.value, selectedYear?.value]);

  const saveSelectedReady = useCallback(async () => {
    const ownerCodes = selectedReadyRows.map((item) => getOwnerCodeFromItem(item)).filter(Boolean);
    return postMonthlyInvoices({
      ownerCodes,
      confirmText: (count) => `Generate monthly invoices for ${count} selected ready owner(s) in ${selectedPeriodLabel}?`,
      successMessage: 'Selected ready invoices generated successfully.',
      failureMessage: 'Failed to generate selected ready invoices.',
      setLoading: setSavingSelectedReady
    });
  }, [postMonthlyInvoices, selectedPeriodLabel, selectedReadyRows]);

  const invoiceSingleReadyRow = useCallback(async (ownerCode) => (
    postMonthlyInvoices({
      ownerCodes: [ownerCode],
      confirmText: () => `Generate monthly invoice for ${ownerCode} in ${selectedPeriodLabel}?`,
      successMessage: 'Ready invoice generated successfully.',
      failureMessage: 'Failed to generate ready invoice.',
      setLoading: setSavingSelectedReady
    })
  ), [postMonthlyInvoices, selectedPeriodLabel]);

  useEffect(() => {
    if (!error || !errorSectionRef.current) {
      return;
    }

    errorSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    errorSectionRef.current.focus({ preventScroll: true });
  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] via-[#fdf5ef] to-[#ede4da] text-slate-800">
      <div className="mx-auto max-w-7xl px-4 py-6 lg:px-6 lg:py-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Button
            onClick={() => navigate('/owners/invoices/monthly')}
            className={neutralInvoiceButtonClass}
          >
            <FaArrowLeft />
            Back to Monthly Invoices
          </Button>
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-4 py-2 text-sm font-semibold text-orange-800">
            <FaFileInvoiceDollar />
            Bulk Monthly Invoice by Cars
          </div>
        </div>

        <div className="mb-6 rounded-3xl border border-orange-100 bg-white/90 p-6 shadow-xl shadow-orange-100/50">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <Typography variant="h3" className="text-3xl font-bold text-[#2a2e30]">
                Bulk Monthly Invoices
              </Typography>
              <Typography className="mt-2 text-sm text-slate-600">
                Select a month, preview every invoiceable owner and their cars, then generate all draft invoices in one save.
              </Typography>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link
                to="/owners/invoices/monthly/reports"
                className="inline-flex items-center gap-2 rounded-full border border-[#E7D6CA] bg-white px-4 py-2 text-xs font-semibold text-[#2a2e30] shadow-sm hover:border-[#FF5733] hover:text-[#FF5733]"
              >
                <FaFileInvoiceDollar />
                Reports
              </Link>
              <Link
                to="/owners/invoices/monthly/guide"
                className="inline-flex items-center gap-2 rounded-full border border-[#E7D6CA] bg-white px-4 py-2 text-xs font-semibold text-[#2a2e30] shadow-sm hover:border-[#FF5733] hover:text-[#FF5733]"
              >
                <FaCalendarAlt />
                Guide
              </Link>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <Typography className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Year</Typography>
              <ReactSelect
                value={selectedYear}
                onChange={(option) => setSelectedYear(option)}
                options={yearOptions}
                styles={selectStyles}
              />
            </div>
            <div>
              <Typography className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Month</Typography>
              <ReactSelect
                value={selectedMonth}
                onChange={(option) => setSelectedMonth(option)}
                options={monthOptions}
                styles={selectStyles}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={loadPreview}
                disabled={loadingPreview}
                className={primaryInvoiceButtonClass}
              >
                <FaPlay />
                {loadingPreview ? 'Loading Preview...' : 'Load All Invoices'}
              </Button>
            </div>
            {/* Bulk clean invoice button removed temporarily */}
          </div>

          <div className="mt-4">
            <Typography className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Invoice Notes
            </Typography>
            <input
              id="invoice-notes"
              name="invoice_notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              aria-label="Invoice notes"
              className="w-full mt-1 h-12 rounded-xl border border-slate-300 bg-white px-4 py-3 shadow-sm"
              placeholder="Add shared notes for the invoice posting..."
            />
            <Typography className="mt-2 text-xs text-slate-500">
              These notes will be sent with the bulk posting request.
            </Typography>
          </div>

          {message && (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {message}
            </div>
          )}
          {error && (
            <div
              ref={errorSectionRef}
              tabIndex={-1}
              className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 outline-none"
            >
              {error}
            </div>
          )}
          {saveSummary && (
            <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              Saved {saveSummary.created} of {saveSummary.total} invoice batch item(s).
            </div>
          )}

          {previewRows.length > 0 && (
            <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-dashed border-emerald-200 bg-emerald-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-slate-700">
                <span className="font-semibold text-slate-900">Ready to invoice</span>
                <span className="ml-2">
                  {selectedReadyCount} selected of {readyInvoiceRows.length}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={toggleSelectAllReady}
                  disabled={!hasReadyInvoiceRows}
                  variant="outlined"
                  className={secondaryInvoiceButtonClass}
                >
                  {allReadySelected ? 'Clear Ready Selection' : 'Select All Ready'}
                </Button>
                <button
                  type="button"
                  onClick={saveSelectedReady}
                  disabled={savingSelectedReady || selectedReadyCount === 0}
                  className={primaryInvoiceButtonClass}
                >
                  <FaSave />
                  {savingSelectedReady ? 'Saving Selected...' : 'Invoice Selected Ready Rows'}
                </button>
              </div>
            </div>
          )}

          {draftInvoiceRows.length > 0 && (
            <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-dashed border-slate-300 bg-white/90 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="text-sm text-slate-700">
                <span className="font-semibold text-slate-900">Draft posted invoices</span>
                <span className="ml-2">{selectedDraftInvoiceRefs.length} selected of {draftInvoiceRows.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={toggleSelectAllDrafts}
                  disabled={!draftInvoiceRows || draftInvoiceRows.length === 0}
                  variant="outlined"
                  className={secondaryInvoiceButtonClass}
                >
                  {selectedDraftInvoiceRefs.length === draftInvoiceRows.length ? 'Clear Draft Selection' : 'Select All Drafts'}
                </Button>
                <button
                  type="button"
                  onClick={markSelectedDraftsSent}
                  disabled={sendingDrafts || selectedDraftInvoiceRefs.length === 0}
                  className={primaryInvoiceButtonClass}
                >
                  <FaSave />
                  {sendingDrafts ? 'Updating...' : 'Mark Selected Drafts as Sent'}
                </button>
              </div>
            </div>
          )}
        </div>

        <Card className="mt-6 shadow-xl">
          <CardBody className="p-0">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                    <FaFileInvoiceDollar />
                    Preview for {selectedPeriodLabel}
                  </div>
                  <Typography className="mt-1 text-sm text-slate-500">
                    Each card below shows the owner invoice, total amount and the cars that make it up.
                  </Typography>
                </div>
                <Button
                  onClick={loadPreview}
                  disabled={loadingPreview}
                  className={neutralInvoiceButtonClass}
                >
                  <FaSyncAlt />
                  Refresh Preview
                </Button>
              </div>
            </div>

            {loadingPreview && previewRows.length === 0 ? (
              <div className="py-14 text-center text-sm text-slate-500">
                Loading monthly invoice previews...
              </div>
            ) : previewRows.length === 0 ? (
              <div className="py-14 text-center text-sm text-slate-500">
                No preview rows loaded yet. Select a month and click Load All Invoices.
              </div>
            ) : (
              <>
                <div className="hidden overflow-hidden lg:block">
                  <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="w-12 px-4 py-3"></th>
                        <th className="px-4 py-3">Owner</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Cars</th>
                        <th className="px-4 py-3">Net</th>
                        <th className="px-4 py-3">Gross</th>
                        <th className="px-4 py-3">Discount</th>
                        <th className="px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {previewRows.map((item) => {
                        const data = item?.data || {};
                        const ownerCode = getOwnerCodeFromItem(item);
                        const canInvoice = item.success && !data.invoice_ref && Boolean(ownerCode);
                        const skipReason = String(item.skip_reason || item.message || 'Skipped').trim();
                        let statusLabel;
                        let statusClass;
                        if (data.invoice_ref) {
                          const s = String(data.invoice_status || '').toUpperCase();
                          if (s === 'DRAFT') {
                            statusLabel = 'Draft';
                            statusClass = 'bg-slate-100 text-slate-700';
                          } else if (s === 'SENT') {
                            statusLabel = 'Sent';
                            statusClass = 'bg-emerald-100 text-emerald-800';
                          } else if (s === 'PAID') {
                            statusLabel = 'Paid';
                            statusClass = 'bg-blue-100 text-blue-800';
                          } else {
                            statusLabel = 'Posted';
                            statusClass = 'bg-slate-200 text-slate-700';
                          }
                        } else {
                          statusLabel = item.success ? 'Ready to Invoice' : 'Skipped';
                          statusClass = item.success ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700';
                        }
                        const carDetails = Array.isArray(data.car_details) ? data.car_details : [];
                        const expanded = expandedOwnerSet.has(ownerCode);

                        return (
                          <React.Fragment key={`${item.owner_code}-${data.invoice_ref || 'preview'}-${data.year || selectedYear?.value}-${data.month || selectedMonth?.value}`}>
                            <tr className="align-top">
                              <td className="px-4 py-4">
                                <button
                                  type="button"
                                  onClick={() => toggleExpandedRow(ownerCode)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:text-slate-900"
                                  aria-label={expanded ? 'Collapse row' : 'Expand row'}
                                >
                                  <FaChevronDown className={`text-xs transition-transform ${expanded ? 'rotate-180' : ''}`} />
                                </button>
                              </td>
                              <td className="px-4 py-4">
                                <div className="font-semibold text-slate-900">
                                  {item.owner_code} {item.owner_name ? `- ${item.owner_name}` : ''}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                  Period {formatPeriodLabel(data.year || selectedYear?.value, data.month || selectedMonth?.value)} · Invoice date {formatDate(data.invoice_date)}
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex flex-col gap-2">
                                  <span className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                                    {statusLabel}
                                  </span>
                                  {canInvoice ? (
                                    <label className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                                      <input
                                        type="checkbox"
                                        checked={selectedReadySet.has(ownerCode)}
                                        onChange={() => toggleReadySelection(ownerCode)}
                                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                      />
                                      Select this row
                                    </label>
                                  ) : (data.invoice_ref && String(data.invoice_status || '').toUpperCase() === 'DRAFT' ? (
                                    <label className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                                      <input
                                        type="checkbox"
                                        checked={selectedDraftSet.has(String(data.invoice_ref || data.trans_id || ''))}
                                        onChange={() => toggleDraftSelection(data.invoice_ref || data.trans_id)}
                                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                      />
                                      Select Draft
                                    </label>
                                  ) : !item.success ? (
                                    <span className="inline-flex w-fit items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                                      Skipped
                                    </span>
                                  ) : null)}
                                </div>
                              </td>
                              <td className="px-4 py-4 font-semibold text-slate-900">{Number(data.car_count || carDetails.length || 0)}</td>
                              <td className="px-4 py-4 font-semibold text-emerald-700">{formatMoney(data.total_amount || data.amount || 0)}</td>
                              <td className="px-4 py-4 font-semibold text-slate-900">{formatMoney(data.gross_amount || 0)}</td>
                              <td className="px-4 py-4 font-semibold text-rose-600">{formatMoney(data.discount_amount || 0)}</td>
                              <td className="px-4 py-4">
                                <div className="flex flex-wrap gap-2">
                                  {canInvoice && (
                                    <button
                                      type="button"
                                      onClick={() => invoiceSingleReadyRow(ownerCode)}
                                      disabled={savingSelectedReady}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-slate-300 hover:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <FaSave />
                                    </button>
                                  )}
                                  {item.success && data.invoice_ref && (
                                    <Link
                                      to={`/owners/transactions/${encodeURIComponent(item.owner_code)}/${encodeURIComponent(data.invoice_ref)}`}
                                      className="inline-flex items-center gap-2 border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white text-sm font-semibold rounded-sm px-3 py-2 transition-colors duration-200"
                                    >
                                      <FaFileInvoiceDollar />
                                      Open Invoice
                                    </Link>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {expanded && (
                              <tr className="bg-slate-50">
                                <td colSpan={8} className="px-4 pb-5">
                                  <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 xl:grid-cols-2">
                                    <div>
                                      {!item.success ? (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                                          <div className="flex items-start gap-2">
                                            <FaTimesCircle className="mt-0.5 text-amber-600" />
                                            <div className="space-y-1">
                                              <div className="font-semibold">Skip reason</div>
                                              <div>{skipReason}</div>
                                              {item.message && item.message !== skipReason && (
                                                <div className="text-xs text-amber-800">{item.message}</div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-900">
                                          <div className="flex items-start gap-2">
                                            <FaCheckCircle className="mt-0.5 text-emerald-600" />
                                            <span>{item.message || 'Preview loaded.'}</span>
                                          </div>
                                        </div>
                                      )}
                                      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice Totals</div>
                                        <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-400">Cars</div>
                                            <div className="font-semibold text-slate-900">{Number(data.car_count || carDetails.length || 0)}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-400">Net</div>
                                            <div className="font-semibold text-emerald-700">{formatMoney(data.total_amount || data.amount || 0)}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-400">Gross</div>
                                            <div className="font-semibold text-slate-900">{formatMoney(data.gross_amount || 0)}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs uppercase tracking-wide text-slate-400">Discount</div>
                                            <div className="font-semibold text-rose-600">{formatMoney(data.discount_amount || 0)}</div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Car Details</div>
                                      <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
                                        <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
                                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                                            <tr>
                                              <th className="px-3 py-2">Plate</th>
                                              <th className="px-3 py-2">Allocation</th>
                                              <th className="px-3 py-2">Days</th>
                                              <th className="px-3 py-2">Gross</th>
                                              <th className="px-3 py-2">Discount</th>
                                              <th className="px-3 py-2">Net</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-100 bg-white">
                                            {carDetails.length > 0 ? carDetails.map((car) => (
                                              <tr key={`${item.owner_code}-${car.plate_no}-${car.allocation_date}`}>
                                                <td className="px-3 py-2 font-semibold text-slate-900">{car.plate_no || '-'}</td>
                                                <td className="px-3 py-2 text-slate-600">{formatDate(car.allocation_date)}</td>
                                                <td className="px-3 py-2 text-slate-600">{car.days_counted ?? '-'}</td>
                                                <td className="px-3 py-2 text-slate-900">{formatMoney(car.gross_amount || 0)}</td>
                                                <td className="px-3 py-2 text-rose-600">{formatMoney(car.discount_amount || 0)}</td>
                                                <td className="px-3 py-2 text-emerald-700">{formatMoney(car.prorated_amount || 0)}</td>
                                              </tr>
                                            )) : (
                                              <tr>
                                                <td colSpan={6} className="px-3 py-4 text-center text-sm text-slate-500">
                                                  No car details available.
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="grid gap-4 p-4 lg:hidden">
                {previewRows.map((item) => {
                  const data = item?.data || {};
                  const ownerCode = getOwnerCodeFromItem(item);
                  const canInvoice = item.success && !data.invoice_ref && Boolean(ownerCode);
                  const skipReason = String(item.skip_reason || item.message || 'Skipped').trim();
                  let statusLabel;
                  let statusClass;
                  if (data.invoice_ref) {
                    const s = String(data.invoice_status || '').toUpperCase();
                    if (s === 'DRAFT') {
                      statusLabel = 'Draft';
                      statusClass = 'bg-slate-100 text-slate-700';
                    } else if (s === 'SENT') {
                      statusLabel = 'Sent';
                      statusClass = 'bg-emerald-100 text-emerald-800';
                    } else if (s === 'PAID') {
                      statusLabel = 'Paid';
                      statusClass = 'bg-blue-100 text-blue-800';
                    } else {
                      statusLabel = 'Posted';
                      statusClass = 'bg-slate-200 text-slate-700';
                    }
                  } else {
                    statusLabel = item.success ? 'Ready to Invoice' : 'Skipped';
                    statusClass = item.success ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700';
                  }
                  const carDetails = Array.isArray(data.car_details) ? data.car_details : [];

                  return (
                    <div key={`${item.owner_code}-${data.invoice_ref || 'preview'}-${data.year || selectedYear?.value}-${data.month || selectedMonth?.value}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-bold text-slate-900">
                            {item.owner_code} {item.owner_name ? `- ${item.owner_name}` : ''}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            Period {formatPeriodLabel(data.year || selectedYear?.value, data.month || selectedMonth?.value)} · Invoice date {formatDate(data.invoice_date)}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                            {statusLabel}
                          </span>
                          {canInvoice ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <label className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                                <input
                                  type="checkbox"
                                  checked={selectedReadySet.has(ownerCode)}
                                  onChange={() => toggleReadySelection(ownerCode)}
                                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                Select this row
                              </label>
                              <button
                                type="button"
                                onClick={() => invoiceSingleReadyRow(ownerCode)}
                                disabled={savingSelectedReady}
                                className={rowInvoiceButtonClass}
                              >
                                <FaSave />
                                Invoice Row
                              </button>
                            </div>
                          ) : (data.invoice_ref && String(data.invoice_status || '').toUpperCase() === 'DRAFT' ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <label className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                                <input
                                  type="checkbox"
                                  checked={selectedDraftSet.has(String(data.invoice_ref || data.trans_id || ''))}
                                  onChange={() => toggleDraftSelection(data.invoice_ref || data.trans_id)}
                                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                Select Draft
                              </label>
                              <button
                                type="button"
                                onClick={async () => {
                                  const items = [{ invoice_ref: data.invoice_ref || data.trans_id, owner_code: item.owner_code || data.owner_code }];
                                  setSendingDrafts(true);
                                  try {
                                    const resp = await axios.post(`${rootUrl}/api/owners/transactions/monthly-invoice/status/bulk`, { items, status: 'SENT' }, { headers: { Authorization: `Bearer ${authToken}` } });
                                    if (resp.data?.success) {
                                      setMessage(resp.data.message || 'Invoice updated');
                                      await loadPreview({ preserveSaveSummary: true });
                                    } else {
                                      setError(resp.data?.message || 'Failed to update invoice');
                                    }
                                  } catch (e) {
                                    setError(getApiErrorMessage(e, 'Failed to update invoice'));
                                  } finally {
                                    setSendingDrafts(false);
                                  }
                                }}
                                className={rowInvoiceButtonClass}
                              >
                                Mark Sent
                              </button>
                            </div>
                          ) : !item.success ? (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                              Skipped
                            </span>
                          ) : null)}
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-xl bg-white p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">Cars</div>
                          <div className="mt-1 font-semibold text-slate-900">{Number(data.car_count || carDetails.length || 0)}</div>
                        </div>
                        <div className="rounded-xl bg-white p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">Net</div>
                          <div className="mt-1 font-semibold text-emerald-700">{formatMoney(data.total_amount || data.amount || 0)}</div>
                        </div>
                        <div className="rounded-xl bg-white p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">Gross</div>
                          <div className="mt-1 font-semibold text-slate-900">{formatMoney(data.gross_amount || 0)}</div>
                        </div>
                        <div className="rounded-xl bg-white p-3">
                          <div className="text-xs uppercase tracking-wide text-slate-500">Discount</div>
                          <div className="mt-1 font-semibold text-rose-600">{formatMoney(data.discount_amount || 0)}</div>
                        </div>
                      </div>

                      {!item.success ? (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          <div className="flex items-start gap-2">
                            <FaTimesCircle className="mt-0.5 text-amber-600" />
                            <div className="space-y-1">
                              <div className="font-semibold">Skip reason</div>
                              <div>{skipReason}</div>
                              {item.message && item.message !== skipReason && (
                                <div className="text-xs text-amber-800">{item.message}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 rounded-xl bg-white p-3 text-sm text-slate-700">
                          <div className="flex items-start gap-2">
                            <FaCheckCircle className="mt-0.5 text-emerald-600" />
                            <span>{item.message || 'Preview loaded.'}</span>
                          </div>
                        </div>
                      )}

                      {carDetails.length > 0 && (
                        <details className="mt-3 rounded-xl border border-slate-200 bg-white">
                          <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700">
                            <span>Car details</span>
                            <FaChevronDown className="text-xs text-slate-500" />
                          </summary>
                          <div className="border-t border-slate-200">
                            <div className="space-y-3 px-4 py-4 md:hidden">
                              {carDetails.map((car) => (
                                <div key={`${item.owner_code}-${car.plate_no}-${car.allocation_date}`} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <div className="text-sm font-semibold text-slate-900">{car.plate_no || '-'}</div>
                                      <div className="text-xs text-slate-500">{formatDate(car.allocation_date)}</div>
                                    </div>
                                    <div className="text-right text-xs text-slate-500">
                                      <div>{car.days_counted ?? '-' } days</div>
                                    </div>
                                  </div>

                                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                    <div className="rounded-lg bg-white p-2">
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Gross</div>
                                      <div className="mt-1 font-semibold text-slate-900">{formatMoney(car.gross_amount || 0)}</div>
                                    </div>
                                    <div className="rounded-lg bg-white p-2">
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Discount</div>
                                      <div className="mt-1 font-semibold text-slate-900">{formatMoney(car.discount_amount || 0)}</div>
                                    </div>
                                    <div className="col-span-2 rounded-lg bg-white p-2">
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Net</div>
                                      <div className="mt-1 font-semibold text-emerald-700">{formatMoney(car.prorated_amount || 0)}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="hidden overflow-x-auto md:block">
                              <table className="min-w-[680px] w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600">
                                  <tr>
                                    <th className="px-4 py-2 text-left font-semibold">Plate</th>
                                    <th className="px-4 py-2 text-left font-semibold">Allocation</th>
                                    <th className="px-4 py-2 text-right font-semibold">Days</th>
                                    <th className="px-4 py-2 text-right font-semibold">Gross</th>
                                    <th className="px-4 py-2 text-right font-semibold">Discount</th>
                                    <th className="px-4 py-2 text-right font-semibold">Net</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {carDetails.map((car) => (
                                    <tr key={`${item.owner_code}-${car.plate_no}-${car.allocation_date}`} className="border-t border-slate-200">
                                      <td className="px-4 py-2 font-medium text-slate-900">{car.plate_no || '-'}</td>
                                      <td className="px-4 py-2 text-slate-600">{formatDate(car.allocation_date)}</td>
                                      <td className="px-4 py-2 text-right text-slate-600">{car.days_counted ?? '-'}</td>
                                      <td className="px-4 py-2 text-right text-slate-600">{formatMoney(car.gross_amount || 0)}</td>
                                      <td className="px-4 py-2 text-right text-slate-600">{formatMoney(car.discount_amount || 0)}</td>
                                      <td className="px-4 py-2 text-right font-semibold text-slate-900">{formatMoney(car.prorated_amount || 0)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </details>
                      )}

                      {item.success && data.invoice_ref && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          <Link
                            to={`/owners/transactions/${encodeURIComponent(item.owner_code)}/${encodeURIComponent(data.invoice_ref)}`}
                            className="inline-flex items-center gap-2 border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white text-sm font-semibold rounded-sm px-3 py-2 transition-colors duration-200"
                          >
                            <FaFileInvoiceDollar />
                            Open Invoice
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default BulkMonthlyInvoices;
